import { SimpleUserRoleSchema } from "@/app/shared/services/volateq-api/api-schemas/user-schemas";
import {
  AnalysisNoteActionType,
  AnalysisNoteAttachmentSchema,
  AnalysisNoteReactionSchema,
  AnalysisNoteSchema,
} from "@/app/shared/services/volateq-api/api-schemas/analysis-note-schema";
import { EvaluationBatchSchema } from "@/app/shared/services/volateq-api/api-schemas/evaluation-batch-schema";
import { escapeHtml } from "@/app/shared/services/helper/string-helper";

export interface NoteMention {
  token: string;
  userId: string;
}

/**
 * The small, fixed set of emojis users may react to a note with — the ~5 most useful
 * reactions. Mirrors ALLOWED_REACTION_EMOJIS on the backend; keep the two in sync.
 */
export const REACTION_EMOJIS = ["👍", "👎", "❤️", "🎉", "😄"];

/** One emoji's reactions on a note, aggregated across users. */
export interface GroupedReaction {
  emoji: string;
  count: number;
  /** Whether the current user is among the reactors (so the chip can be highlighted/toggled). */
  reactedByMe: boolean;
  /** Display names of the reactors, for the chip tooltip. */
  reactorNames: string[];
}

/** Which of the notes thread's composers holds unsent content. */
export type UnsavedComposerDraft = "note" | "whatsapp";

/** Current state of the note composer, used to detect unsent content. */
export interface ComposerDraft {
  /** Text currently in the composer textarea. */
  text: string | null;
  /** Number of files picked or pasted into the composer but not uploaded yet. */
  newFileCount: number;
  /** The note being edited, or null while composing a new note. */
  editedNote: AnalysisNoteSchema | null;
  /** Ids of the edited note's existing attachments still kept in the composer. */
  keptExistingAttachmentIds: string[];
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

export class AnalysisNotesHelper {
  /**
   * Returns the @-mention query the cursor currently sits in (the word after an
   * `@` that is at the start of the text or preceded by whitespace), or null.
   */
  static getMentionQuery(text: string, cursorPosition: number): { query: string; start: number } | null {
    const upToCursor = text.slice(0, cursorPosition);
    const match = upToCursor.match(/(?:^|\s)@([^\s@]*)$/);
    if (!match) {
      return null;
    }

    const query = match[1];
    return { query, start: cursorPosition - query.length - 1 };
  }

  /** Builds the contiguous (whitespace-free) token inserted into the note for a mention. */
  static buildMentionToken(user: SimpleUserRoleSchema): string {
    const handle = `${user.first_name || ""}${user.last_name || ""}`.replace(/\s+/g, "");
    return "@" + (handle || (user.email || "").split("@")[0] || user.id);
  }

  /** Inserts a mention token at `start`, replacing the partial query, and returns the new text. */
  static insertMention(text: string, start: number, queryLength: number, token: string): string {
    const before = text.slice(0, start);
    const after = text.slice(start + queryLength + 1);
    return `${before}${token} ${after}`;
  }

  /** Resolves which selected mentions are still present in the final text. */
  static resolveMentionedUserIds(text: string, mentions: NoteMention[]): string[] {
    const userIds = new Set<string>();
    for (const mention of mentions) {
      if (text.includes(mention.token)) {
        userIds.add(mention.userId);
      }
    }
    return Array.from(userIds);
  }

  /**
   * Re-resolves which users are mentioned in a (possibly edited) note by matching each user's
   * canonical mention token against the text. Used when editing a note, where the original
   * token→user map from the compose-time autocomplete is no longer available.
   */
  static resolveMentionedUserIdsFromText(text: string, users: SimpleUserRoleSchema[]): string[] {
    const userIds = new Set<string>();
    for (const user of users) {
      if (text.includes(AnalysisNotesHelper.buildMentionToken(user))) {
        userIds.add(user.id);
      }
    }
    return Array.from(userIds);
  }

  static fullName(user: { first_name?: string; last_name?: string; email?: string } | null): string {
    if (!user) {
      return "";
    }
    return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || "";
  }

  static renderDescriptionHtml(
    description: string,
    mentionedUsers: SimpleUserRoleSchema[] = [],
    currentUserId: string | null = null,
    linkifyUrls = true,
  ): string {
    let html = escapeHtml(description);
    if (linkifyUrls) {
      html = AnalysisNotesHelper.linkifyUrls(html);
    }
    html = html.replace(/(\r\n|\r|\n)/g, "<br>");
    html = AnalysisNotesHelper.wrapMentions(html, mentionedUsers, currentUserId);
    return html;
  }

  private static wrapMentions(
    html: string,
    mentionedUsers: SimpleUserRoleSchema[],
    currentUserId: string | null,
  ): string {
    let result = html;
    const mentionEntries = [...mentionedUsers]
      .map(user => ({
        token: escapeHtml(AnalysisNotesHelper.buildMentionToken(user)),
        className: user.id === currentUserId ? "app-note-mention-self" : "app-note-mention",
      }))
      .sort((a, b) => b.token.length - a.token.length);

    for (const { token, className } of mentionEntries) {
      result = AnalysisNotesHelper.wrapToken(result, token, className);
    }

    return result.replace(/(^|\s)(@[^\s<]+)/g, '$1<span class="app-note-mention">$2</span>');
  }

  private static wrapToken(html: string, token: string, className: string): string {
    if (!html.includes(token)) {
      return html;
    }
    return html.split(token).join(`<span class="${className}">${token}</span>`);
  }

  private static readonly NOTE_URL_PATTERN =
    /(?:https?:\/\/|ftp:\/\/|www\.)[^\s<]+|\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?::\d{1,5})?(?:[/?#][^\s<]*)?/gi;

  private static linkifyUrls(html: string): string {
    return html.replace(AnalysisNotesHelper.NOTE_URL_PATTERN, match => {
      const trimmed = match.replace(/[.,;:!?)]+$/, "");
      const trailing = match.slice(trimmed.length);
      const href = AnalysisNotesHelper.normalizeNoteLinkHref(trimmed);
      if (!href) {
        return match;
      }
      return `<a class="app-note-link" href="${href}" target="_blank" rel="noopener noreferrer">${trimmed}</a>${trailing}`;
    });
  }

  private static normalizeNoteLinkHref(url: string): string | null {
    if (/^(?:https?|ftp):\/\//i.test(url)) {
      return url;
    }
    if (/^www\./i.test(url)) {
      return `https://${url}`;
    }
    if (/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}/i.test(url)) {
      return `https://${url}`;
    }
    return null;
  }

  static isImageAttachment(attachment: AnalysisNoteAttachmentSchema): boolean {
    return IMAGE_EXTENSIONS.test(attachment.file_name);
  }

  static formatDateTime(isoDate: string): string {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return isoDate;
    }
    return date.toLocaleString();
  }

  /** Looks up a note by id in the loaded thread (live resolve for reply quotes). */
  static findNoteById(notes: AnalysisNoteSchema[], noteId: string | null): AnalysisNoteSchema | null {
    if (!noteId) {
      return null;
    }
    return notes.find(note => note.id === noteId) ?? null;
  }

  /**
   * Compact signature of everything the thread renders, used to tell an auto refresh that
   * actually brought something new from one that did not. The presigned attachment urls are
   * left out on purpose: the backend signs them per request, so they differ on every fetch
   * without the thread having changed.
   */
  static threadFingerprint(notes: AnalysisNoteSchema[]): string {
    return JSON.stringify(
      notes.map(note => [
        note.id,
        note.description,
        note.action_type,
        note.eval_batch_num,
        note.edited_at,
        note.parent_note_id,
        note.attachments.map(attachment => attachment.id),
        note.mentioned_users.map(user => user.id),
        note.reactions.map(reaction => [reaction.emoji, reaction.user?.id ?? null]),
      ]),
    );
  }

  /**
   * Keeps the already loaded presigned url of every attachment the refetched thread still
   * carries. A freshly signed url of the same file would change the <img> src and make the
   * browser download and repaint the image again on every auto refresh.
   */
  static carryOverAttachmentUrls(
    previousNotes: AnalysisNoteSchema[],
    refetchedNotes: AnalysisNoteSchema[],
  ): AnalysisNoteSchema[] {
    const knownUrls = new Map<string, string>();
    for (const note of previousNotes) {
      for (const attachment of note.attachments) {
        if (attachment.url) {
          knownUrls.set(attachment.id, attachment.url);
        }
      }
    }

    if (knownUrls.size === 0) {
      return refetchedNotes;
    }

    for (const note of refetchedNotes) {
      for (const attachment of note.attachments) {
        attachment.url = knownUrls.get(attachment.id) ?? attachment.url;
      }
    }

    return refetchedNotes;
  }

  /**
   * Whether the composer holds content that would be lost when the notes thread is closed:
   * newly attached files, any text while composing a new note, or — while editing an existing
   * note — text or attachments deviating from what is stored on that note.
   */
  static hasUnsavedComposerContent(draft: ComposerDraft): boolean {
    if (draft.newFileCount > 0) {
      return true;
    }

    const text = (draft.text || "").trim();
    if (!draft.editedNote) {
      return text !== "";
    }

    const removedAnAttachment = draft.editedNote.attachments.some(
      attachment => !draft.keptExistingAttachmentIds.includes(attachment.id),
    );
    return removedAnAttachment || text !== (draft.editedNote.description || "").trim();
  }

  /** Short single-line preview of a note body for reply quotes. */
  static replyPreviewText(description: string, maxLength = 120): string {
    const compact = description.replace(/\s+/g, " ").trim();
    if (compact.length <= maxLength) {
      return compact;
    }
    return compact.slice(0, maxLength - 1).trimEnd() + "…";
  }

  /**
   * Extracts image files from a paste (clipboard) event, e.g. a copied screenshot.
   * Each image is given a unique name so attachments of the same note don't collide.
   */
  static extractImageFilesFromClipboard(event: ClipboardEvent): File[] {
    const items = event.clipboardData?.items;
    if (!items) {
      return [];
    }

    const files: File[] = [];
    Array.from(items).forEach((item, index) => {
      if (item.kind !== "file" || !item.type.startsWith("image/")) {
        return;
      }

      const blob = item.getAsFile();
      if (!blob) {
        return;
      }

      const extension = item.type.split("/")[1] || "png";
      const fileName = `screenshot-${Date.now()}-${index}.${extension}`;
      files.push(new File([blob], fileName, { type: item.type }));
    });

    return files;
  }

  /** Filters notes by action type and evaluation batch. An empty selection means "no restriction". */
  static filterNotes(
    notes: AnalysisNoteSchema[],
    selectedTypes: AnalysisNoteActionType[],
    selectedEbNums: number[],
  ): AnalysisNoteSchema[] {
    if (selectedTypes.length === 0 && selectedEbNums.length === 0) {
      return notes;
    }
    return notes.filter(
      note =>
        (selectedTypes.length === 0 || selectedTypes.includes(note.action_type)) &&
        (selectedEbNums.length === 0 ||
          (note.eval_batch_num !== null && selectedEbNums.includes(note.eval_batch_num))),
    );
  }

  /** True for an error or info the evaluation pipeline sent to the Ops user, logged for every admin. */
  static isEvaluationMessage(note: AnalysisNoteSchema): boolean {
    return note.action_type === "EVALUATION_ERROR" || note.action_type === "EVALUATION_INFO";
  }

  /** Returns the distinct evaluation batch numbers of a plant, sorted ascending. */
  static evalBatchNums(evaluationBatches: EvaluationBatchSchema[]): number[] {
    return Array.from(new Set(evaluationBatches.map(batch => batch.num))).sort((a, b) => a - b);
  }

  /**
   * Aggregates a note's reactions by emoji, preserving the order in which each emoji first
   * appears, and flags those the current user has reacted with.
   */
  static groupReactions(
    reactions: AnalysisNoteReactionSchema[],
    currentUserId: string | null,
  ): GroupedReaction[] {
    const grouped = new Map<string, GroupedReaction>();

    for (const reaction of reactions) {
      let group = grouped.get(reaction.emoji);
      if (!group) {
        group = { emoji: reaction.emoji, count: 0, reactedByMe: false, reactorNames: [] };
        grouped.set(reaction.emoji, group);
      }

      group.count += 1;
      group.reactorNames.push(AnalysisNotesHelper.fullName(reaction.user));
      if (reaction.user && currentUserId !== null && reaction.user.id === currentUserId) {
        group.reactedByMe = true;
      }
    }

    return Array.from(grouped.values());
  }

  static filterUsers(users: SimpleUserRoleSchema[], query: string, limit = 6): SimpleUserRoleSchema[] {
    const unlockedUsers = users.filter(user => !user.is_locked);
    const normalizedQuery = query.trim().toLowerCase();
    const matches = normalizedQuery
      ? unlockedUsers.filter(user =>
          AnalysisNotesHelper.fullName(user).toLowerCase().includes(normalizedQuery) ||
          (user.email || "").toLowerCase().includes(normalizedQuery))
      : unlockedUsers;

    return matches.slice(0, limit);
  }
}
